
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from '@/components/ui/button';
import { Printer, RefreshCw } from 'lucide-react';
import { TeacherPaymentHistory } from '@/components/teachers/TeacherPaymentHistory';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { getTeachers } from '@/services/db/teachers';
import { printTeacherHistoryAsJPG, printTeacherHistoryAsPDF } from '@/utils/printing/historyPrinting';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import AdminFormatDialog from '@/components/admin/AdminFormatDialog';

const TeacherHistoryTab = () => {
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  
  // Fetch all teachers
  const { data: teachers = [], isLoading, error, refetch } = useQuery({
    queryKey: ['teachers'],
    queryFn: getTeachers,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Reset selection if there's an error loading teachers
  useEffect(() => {
    if (error) {
      setSelectedTeacherId("");
      toast.error("Failed to load teachers");
    }
  }, [error]);

  const handleRefresh = async () => {
    if (!selectedTeacherId) {
      toast.error("Please select a teacher first");
      return;
    }
    
    setIsRefreshing(true);
    try {
      await refetch();
      toast.success("Teacher history refreshed");
    } catch (error) {
      console.error("Error refreshing teacher history:", error);
      toast.error("Failed to refresh history");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePrintHistory = async (format: 'jpg' | 'pdf') => {
    if (!selectedTeacherId) {
      toast.error("Please select a teacher first");
      return;
    }
    
    const selectedTeacher = teachers.find(t => t.id === selectedTeacherId);
    if (!selectedTeacher) {
      toast.error("Teacher not found");
      return;
    }

    try {
      console.log(`🖨️ Starting ${format.toUpperCase()} download for teacher history...`);
      
      // Get actual payment history for this teacher from localStorage
      const paymentsKey = `teacher_payments_${selectedTeacherId}`;
      const storedPayments = JSON.parse(localStorage.getItem(paymentsKey) || '[]');
      
      // If no stored payments, create sample data based on teacher info
      let payments = storedPayments.length > 0 ? storedPayments : [
        {
          id: 1,
          date: new Date().toISOString(),
          amount: 25000,
          paymentPeriod: new Date().toLocaleDateString('default', { month: 'long', year: 'numeric' }),
          method: 'Cash',
          status: 'Completed',
          paymentType: 'Salary'
        }
      ];

      // Limit to 12 records per page as requested
      const limitedPayments = payments.slice(0, 12);

      console.log('📊 Teacher payment data (limited to 12 records):', limitedPayments);
      
      if (format === 'jpg') {
        await printTeacherHistoryAsJPG(selectedTeacher, limitedPayments);
      } else {
        await printTeacherHistoryAsPDF(selectedTeacher, limitedPayments);
      }
      console.log(`✅ ${format.toUpperCase()} download completed successfully`);
    } catch (error) {
      console.error(`❌ Error downloading teacher history ${format.toUpperCase()}:`, error);
      toast.error(`Failed to download teacher history as ${format.toUpperCase()}`);
    }
  };

  // Helper function to get department display name
  const getDepartmentName = (teacher: any) => {
    if (teacher.department) return teacher.department;
    if (teacher.departments && teacher.departments.length > 0) {
      return typeof teacher.departments[0] === 'string' ? 
        teacher.departments[0] : 
        teacher.departments[0]?.name;
    }
    return 'Not Assigned';
  };

  const getSelectedTeacher = () => {
    return teachers.find(t => t.id === selectedTeacherId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Teacher History Records</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={selectedTeacherId} onValueChange={setSelectedTeacherId}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select a teacher" />
              </SelectTrigger>
              <SelectContent>
                {isLoading ? (
                  <SelectItem value="loading" disabled>Loading teachers...</SelectItem>
                ) : error ? (
                  <SelectItem value="error" disabled>Error loading teachers</SelectItem>
                ) : teachers.length === 0 ? (
                  <SelectItem value="empty" disabled>No teachers found</SelectItem>
                ) : (
                  teachers.map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.firstName} {teacher.lastName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleRefresh} 
                disabled={!selectedTeacherId || isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              {selectedTeacherId && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowFormatDialog(true)}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Download History
                </Button>
              )}
            </div>
          </div>

          {selectedTeacherId ? (
            <div className="space-y-6">
              <TeacherPaymentHistory teacherId={selectedTeacherId} />
              
              {/* Display teacher details */}
              {getSelectedTeacher() && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Teacher Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Information</TableHead>
                          <TableHead>Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Department</TableCell>
                          <TableCell>{getDepartmentName(getSelectedTeacher())}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Subjects</TableCell>
                          <TableCell>{getSelectedTeacher()?.subjects?.join(', ') || 'None'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Status</TableCell>
                          <TableCell>
                            <Badge className={`${
                              getSelectedTeacher()?.status?.toLowerCase() === 'active' ? 'bg-green-100 text-green-800' : 
                              getSelectedTeacher()?.status?.toLowerCase() === 'inactive' ? 'bg-red-100 text-red-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {getSelectedTeacher()?.status || 'Active'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Joining Date</TableCell>
                          <TableCell>
                            {getSelectedTeacher()?.joinDate ? 
                              new Date(getSelectedTeacher()!.joinDate).toLocaleDateString() : 
                              'Not Available'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              Select a teacher to view their history
            </div>
          )}
        </div>

        <AdminFormatDialog
          isOpen={showFormatDialog}
          onClose={() => setShowFormatDialog(false)}
          onDownload={handlePrintHistory}
          downloadType="list"
        />
      </CardContent>
    </Card>
  );
};

export default TeacherHistoryTab;
